import { Group } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { disposeObject3DResources as disposeMeshes } from "../models/shared-model-resources";
import { RackPost, RackPostDirection } from "./rack-post";
import { RackPostColumn } from "./rack-post-column";

type RackPostRowPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type RackPostRowOptions = {
  crossbarZOffset?: number;
  direction?: RackPostDirection;
  heightCellCount?: number;
  lengthCellCount?: number;
  position?: RackPostRowPosition;
  totalHeight?: number;
  totalLength?: number;
};

const defaultRackPostRowOptions = {
  crossbarZOffset: 0.0185,
  direction: RackPostDirection.Front,
  heightCellCount: 1,
  lengthCellCount: 1,
  totalHeight: 1,
  totalLength: 1
};

export class RackPostRow extends Group {
  private columns: RackPostColumn[] = [];
  private crossbarZOffset: number;
  private direction: RackPostDirection;
  private heightCellCount: number;
  private lengthCellCount: number;
  private posts: RackPost[] = [];
  private totalHeight: number;
  private totalLength: number;

  constructor({
    crossbarZOffset = defaultRackPostRowOptions.crossbarZOffset,
    direction = defaultRackPostRowOptions.direction,
    heightCellCount = defaultRackPostRowOptions.heightCellCount,
    lengthCellCount = defaultRackPostRowOptions.lengthCellCount,
    position = {},
    totalHeight = defaultRackPostRowOptions.totalHeight,
    totalLength = defaultRackPostRowOptions.totalLength
  }: RackPostRowOptions = {}) {
    super();
    this.crossbarZOffset = crossbarZOffset;
    this.direction = direction;
    this.heightCellCount = heightCellCount;
    this.lengthCellCount = lengthCellCount;
    this.totalHeight = totalHeight;
    this.totalLength = totalLength;
    this.name = `rack-post-row-${direction}`;
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
  }

  static async create(
    options: RackPostRowOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const row = new RackPostRow(options);
    await row.load(loader);
    return row;
  }

  setDirection(direction: RackPostDirection) {
    this.direction = direction;
    this.name = `rack-post-row-${direction}`;
    this.posts.forEach((post) => {
      post.setDirection(direction);
    });
    this.columns.forEach((column) => {
      column.setDirection(direction);
    });
  }

  setCrossbarZOffset(crossbarZOffset: number) {
    this.crossbarZOffset = crossbarZOffset;
    this.columns.forEach((column) => {
      column.setCrossbarZOffset(crossbarZOffset);
    });
  }

  setHeightCellCount(heightCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.heightCellCount = heightCellCount;
    return this.updatePostHeights(loader);
  }

  setLengthCellCount(lengthCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.lengthCellCount = lengthCellCount;
    return this.reloadPosts(loader);
  }

  setRackRowPosition(position: RackPostRowPosition) {
    this.position.set(
      position.x ?? this.position.x,
      position.y ?? this.position.y,
      position.z ?? this.position.z
    );
  }

  setTotalHeight(totalHeight: number, loader: ModelAssetLoader = modelAssetLibrary) {
    this.totalHeight = totalHeight;
    return this.updatePostHeights(loader);
  }

  setTotalLength(totalLength: number) {
    this.totalLength = totalLength;
    this.updatePostXPositions();
    this.updateColumnSpanPositions();
  }

  private async load(loader: ModelAssetLoader) {
    await this.reloadPosts(loader);
  }

  private async reloadPosts(loader: ModelAssetLoader) {
    this.columns.forEach((column) => {
      this.remove(column);
      disposeMeshes(column);
    });
    this.columns = [];
    this.posts.forEach((post) => {
      this.remove(post);
      disposeMeshes(post);
    });

    const postXPositions = createPostXPositions(this.totalLength, this.lengthCellCount);
    this.posts = await Promise.all(
      postXPositions.map((x) =>
        RackPost.create(
          {
            angleIronHeightCellCount: this.heightCellCount,
            direction: this.direction,
            height: this.totalHeight,
            position: { x }
          },
          loader
        )
      )
    );

    this.add(...this.posts);
    await this.reloadColumns(loader);
  }

  private async updatePostHeights(loader: ModelAssetLoader) {
    this.posts.forEach((post) => {
      post.setHeight(this.totalHeight);
    });

    await Promise.all(
      this.posts.map((post) => post.setAngleIronHeightCellCount(this.heightCellCount, loader))
    );
    await this.updateColumnsForHeight(loader);
  }

  private updatePostXPositions() {
    const postXPositions = createPostXPositions(this.totalLength, this.lengthCellCount);

    this.posts.forEach((post, index) => {
      post.setRackPosition({ x: postXPositions[index] });
    });
  }

  private async reloadColumns(loader: ModelAssetLoader) {
    this.columns.forEach((column) => {
      this.remove(column);
      disposeMeshes(column);
    });
    this.columns = [];

    const firstPost = this.posts[0];

    if (!firstPost) {
      return;
    }

    const cellLength = this.totalLength / this.lengthCellCount;
    const postWidth = firstPost.getBodyWidth();
    const postBaseY = firstPost.getPostBaseY();
    const postBodyHeight = firstPost.getPostBodyHeight();

    this.columns = await Promise.all(
      Array.from({ length: this.lengthCellCount }, (_, index) =>
        RackPostColumn.create(
          {
            crossbarZOffset: this.crossbarZOffset,
            direction: this.direction,
            heightCellCount: this.heightCellCount,
            position: { x: cellLength * index },
            postBaseY,
            postBodyHeight,
            postWidth,
            spanLength: cellLength
          },
          loader
        )
      )
    );
    this.add(...this.columns);
  }

  private async updateColumnsForHeight(loader: ModelAssetLoader) {
    const firstPost = this.posts[0];

    if (!firstPost) {
      return;
    }

    const postBaseY = firstPost.getPostBaseY();
    const postBodyHeight = firstPost.getPostBodyHeight();

    await Promise.all(
      this.columns.map((column) => column.setHeightCellCount(this.heightCellCount, loader))
    );
    this.columns.forEach((column) => {
      column.setHeightMetrics(postBaseY, postBodyHeight);
    });
  }

  private updateColumnSpanPositions() {
    const firstPost = this.posts[0];

    if (!firstPost) {
      return;
    }

    const cellLength = this.totalLength / this.lengthCellCount;
    const postWidth = firstPost.getBodyWidth();

    this.columns.forEach((column, index) => {
      column.setColumnPosition({ x: cellLength * index });
      column.setPostWidth(postWidth);
      column.setSpanLength(cellLength);
    });
  }
}

function createPostXPositions(totalLength: number, lengthCellCount: number) {
  const cellLength = totalLength / lengthCellCount;

  return Array.from({ length: lengthCellCount + 1 }, (_, index) => cellLength * index);
}
